/* validation.js — placement validation. FULL faithful port of validatePlacement
 * (lhc-app.html ~31963): overhang/terminal radius, cabinet-vs-cabinet 3D collision,
 * oriented SAT cabinet↔wall overlap, same-wall door/window block, hob→wall-unit clearance,
 * and non-blocking window-cover / door-swing warnings.
 *
 * NB on out-of-wall: the original does NOT reject "past the wall end" here — the snap/clamp
 * keeps absLeft inside the inner span (see isWithinWall + state.addCabinet). Pass opts.bounds
 * to additionally enforce that span as a hard error.
 */
import { RULE_EPS, LHC_RULES } from "./constants";
import { pairMinGapLR, golaChannelsFor, channelIntrusion } from "./golaClearance";
import { panelSpanOf } from "./sidePanelClearance";
import { lhcVOverlap } from "./collision";
import { placementAABB, placementOBB } from "./placement";
import { aabbOverlap, obbOverlap, cabWallOverlap } from "./collision";
import { wallInnerSpan } from "./snapping";
import { openingAABB, doorSwingAABB, cabOpeningBlock } from "./openings";
import { effDef } from "./pricing";
import { footprintViolation } from "./containment";

const roleLabel = (def) =>
  def && def.role === "cabinet-wall" ? "wall unit"
  : def && def.role === "cabinet-tall" ? "tall unit"
  : "cabinet";

/** True if absLeft keeps the cabinet inside its wall's legal inner span (the snap-clamp span). */
export function isWithinWall(p, def, walls) {
  const wall = walls.find((w) => w.id === p.wallId);
  if (!wall || !def) return true;
  const sp = wallInnerSpan(wall, walls);
  const ps = panelSpanOf(p, def);                 // the side panels must fit inside the wall too
  const lo = sp.off0 - ps.lo;
  const hi = sp.len - sp.off1 - ps.hi;
  if (hi < lo) return false;
  return p.absLeft >= lo - RULE_EPS && p.absLeft <= hi + RULE_EPS;
}

export function validatePlacement(candidate, placements, lib, walls, wizard, openings = [], opts = {}) {
  const warnings = [];
  const ovr = !!(candidate.config && candidate.config.collisionOverride);

  if (candidate.island && typeof candidate.overhangMm === "number" && candidate.overhangMm > 300 && !candidate.supportLeg) {
    return { ok: false, reason: "Overhang " + candidate.overhangMm + "mm requires a support leg" };
  }
  if (typeof candidate.terminalRadiusMm === "number") {
    if (candidate.terminalRadiusMm < 0 || candidate.terminalRadiusMm > 1200) {
      return { ok: false, reason: "Terminal radius must be 0–1200 mm (" + candidate.terminalRadiusMm + " given)" };
    }
  }

  const cdef = effDef(candidate, lib);
  if (!cdef) return { ok: true };
  const cBox = placementAABB(candidate, cdef, walls);
  if (!cBox) return { ok: true };

  if (opts.bounds && candidate.wallId && !isWithinWall(candidate, cdef, walls)) {
    return { ok: false, reason: "Won't fit — outside the wall" };
  }

  // 1 — 3D collision against every other placement. The AABB is the cheap reject, the
  // ORIENTED footprint the verdict (batch 7, 2026-09-28): on a wall at any angle but
  // 0/90° the axis-aligned boxes of two TOUCHING neighbours overlap by up to 30% of a
  // unit — the "says collision even though it fits" report.
  if (!ovr) {
    const cObb = placementOBB(candidate, cdef, walls);
    for (const other of placements) {
      if (other.id === candidate.id) continue;
      const odef = effDef(other, lib);
      if (!odef) continue;
      const oBox = placementAABB(other, odef, walls);
      if (!oBox) continue;
      if (aabbOverlap(cBox, oBox) && obbOverlap(cObb, placementOBB(other, odef, walls))) {
        return { ok: false, reason: `Won't fit — overlaps ${roleLabel(odef)} ${odef.code || odef.name || ""}`.trim() };
      }
    }
  }

  // 1a2 — GOLA CLEARANCE hard gate (2026-08-22 mandate): the vertical gola
  // profile is PHYSICAL — a neighbour on the same wall may never stand closer
  // than 30mm to a gola side. Enforced on every commit path (drag, drop,
  // numeric moves, autosolve) through this ONE gate.
  if (!ovr && candidate.wallId && !candidate.island) {
    for (const other of placements) {
      if (!other || other.id === candidate.id || (other.wallId || null) !== candidate.wallId) continue;
      const odef = effDef(other, lib);
      if (!odef || !lhcVOverlap(candidate, other, lib)) continue;
      const c0 = Number(candidate.absLeft) || 0, c1 = c0 + (cdef.width || 0);
      const o0 = Number(other.absLeft) || 0, o1 = o0 + (odef.width || 0);
      const need = c1 <= o0 + 1e-9
        ? pairMinGapLR(candidate, cdef, other, odef)   // candidate LEFT of other
        : o1 <= c0 + 1e-9
          ? pairMinGapLR(other, odef, candidate, cdef) // candidate RIGHT of other
          : 0;                                          // overlapping — step 1 already handles
      if (need > 0) {
        const gap = c1 <= o0 + 1e-9 ? o0 - c1 : c0 - o1;
        if (gap < need - RULE_EPS) {
          return { ok: false, reason: "Won't fit — the gola needs " + Math.round(need * 1000) + "mm clearance" };
        }
      }
    }
  }

  // 1a3 — GOLA CHANNEL gate (2026-08-30 user: "everything the gola touches
  // adds exactly 30mm"): an extended post (Height / Auto-connect) is a 30mm
  // channel over its FULL height — a wall unit the post passes may not stand
  // inside it any more than the base neighbour may. Same ONE gate discipline.
  if (!ovr && candidate.wallId && !candidate.island) {
    for (const ch of golaChannelsFor(candidate, cdef, placements, (o) => effDef(o, lib))) {
      if (channelIntrusion(candidate, cdef, ch) > RULE_EPS) {
        return { ok: false, reason: "Won't fit — the gola channel needs 30mm clearance here" };
      }
    }
  }

  // 1b — oriented SAT against walls
  if (!ovr) {
    const wHit = cabWallOverlap(candidate, lib, walls);
    if (wHit) return { ok: false, reason: `Won't fit — overlaps wall ${wHit}` };
  }

  // 1d — ROOM CONTAINMENT (user mandate): the cabinet's footprint polygon must
  // stay fully inside the room its wall borders. Final guarantee on top of the
  // span clamp — covers wrong-side corner placements the clamp can't see.
  if (!ovr && candidate.wallId && !candidate.island) {
    if (footprintViolation(candidate, cdef, walls))
      return { ok: false, reason: "Won't fit — outside the room" };
  }

  // 1c — never block a door or window on the same wall
  if (!ovr) {
    const oHit = cabOpeningBlock(candidate, cBox, openings);
    if (oHit) return { ok: false, reason: `Won't fit — blocks ${oHit}` };
  }

  // 2 — hob clearance: a wall unit (not a hood) above a hob
  if (cdef.role === "cabinet-wall" && cdef.subtype !== "hood") {
    for (const other of placements) {
      if (other.id === candidate.id) continue;
      const od = effDef(other, lib);
      if (!od || od.subtype !== "hob") continue;
      const oBox = placementAABB(other, od, walls);
      if (!oBox) continue;
      const xOverlap = cBox.minX < oBox.maxX - RULE_EPS && cBox.maxX > oBox.minX + RULE_EPS;
      const zOverlap = cBox.minZ < oBox.maxZ - RULE_EPS && cBox.maxZ > oBox.minZ + RULE_EPS;
      if (!xOverlap || !zOverlap) continue;
      const gap = cBox.minY - oBox.maxY;
      const isGas = od.subtype === "hob-gas" || !!(other.config && other.config.fuel === "gas");
      if (gap < LHC_RULES.HOB_CLEAR_ELECTRIC - RULE_EPS) {
        return { ok: false, reason: `Won't fit — needs at least ${Math.round(LHC_RULES.HOB_CLEAR_ELECTRIC * 100)}cm above the hob (use a hood)` };
      }
      if (gap < LHC_RULES.HOB_CLEAR_GAS - RULE_EPS && isGas) {
        return { ok: false, reason: `Won't fit — gas hob needs ${Math.round(LHC_RULES.HOB_CLEAR_GAS * 100)}cm clearance (use a hood)` };
      }
    }
  }

  // 3 — non-blocking opening warnings
  for (const op of openings) {
    if (op.type === "window" && (cdef.role === "cabinet-wall" || cdef.role === "cabinet-tall" || cdef.role === "cabinet-stack" || cdef.role === "cabinet-bridge")) {
      const oBox = openingAABB(op, walls);
      if (oBox && aabbOverlap(cBox, oBox)) warnings.push("⚠ " + (cdef.code || cdef.name) + " overlaps a window — check on site");
    }
    if (op.type === "door") {
      const swing = doorSwingAABB(op, walls);
      if (swing && aabbOverlap(cBox, swing)) warnings.push("⚠ " + (cdef.code || cdef.name) + " sits in the door swing — check clearance");
    }
  }

  return { ok: true, warnings };
}
